import { Tooltip } from 'antd';
import { Row, Col } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import CcxComponentProps from '../../../../core/CcxComponent';
import useContentDeployWizard from '../../../../core/hooks/useContentDeployWizard';
import CloudProvider from '../../../../types/CloudProvider';
import DeploymentsItem from '../../../../types/DeploymentsItem';
import AppFlagIcon from '../../../ccx/common/AppFlagIcon';
import AppLogoBrandAws from '../../../ccx/common/AppLogoBrandAws';
import AppLogoBrandCityCloud from '../../../ccx/common/AppLogoBrandCityCloud';
import AppLogoBrandGoogleCloud from '../../../ccx/common/AppLogoBrandGoogleCloud';
import AppLogoBrandLintasarta from '../../../ccx/common/AppLogoBrandLintasarta';
import AppLogoBrandElastx from '../../../ccx/common/AppLogoBrandElastx';
import AppLogoBrandSafespring from '../../../ccx/common/AppLogoBrandSafespring';
import AppLogoBrandVultr from '../../../ccx/common/AppLogoBrandVultr';
import SmallContainer from '../../../ccx/common/SmallContainer';
import CcxIconInfoCircleOutlined from '../../../ccx/icons/CcxIconInfoCircleOutlined';
import LazyLoader from '../../../LazyLoader';
import styles from './DataStoreOverviewCloudInformation.module.less';
import AppLogoBrandSyseleven from '../../../ccx/common/AppLogoBrandSyseleven';

interface Props extends CcxComponentProps {
    dataStore?: DeploymentsItem;
}

function DataStoreOverviewCloudInformation({
    dataStore,
    testId = 'DataStoreOverviewCloudInformation',
}: Props): ReactElement {
    const [cards, setCards] = useState<any>([]);
    const [countryCode, setCountryCode] = useState<string | undefined>(
        undefined
    );
    const { deploymentOptions } = useContentDeployWizard();

    const getCloudProviderLogo = (p: CloudProvider) => {
        if (p.isAws()) return <AppLogoBrandAws height={15} width={24} />;
        if (p.isSafespring())
            return <AppLogoBrandSafespring height={18} width={50} />;
        if (p.isCityCloud())
            return <AppLogoBrandCityCloud height={15} width={50} />;
        if (p.isGoogle())
            return <AppLogoBrandGoogleCloud height={15} width={24} />;
        if (p.isLintasarta())
            return <AppLogoBrandLintasarta height={15} width={24} />;
        if (p.isVultr()) return <AppLogoBrandVultr height={15} width={24} />;
        if (p.isElastx()) return <AppLogoBrandElastx height={15} width={24} />;
        if (p.isSyseleven())
            return <AppLogoBrandSyseleven height={15} width={24} />;

        return null;
    };

    useEffect(() => {
        if (deploymentOptions && dataStore) {
            setCountryCode(
                deploymentOptions
                    ?.getCloudRegions(dataStore.getCloudProvider().code)
                    ?.filter((r: any) => r.code === dataStore.cloudRegion)[0]
                    ?.countryCode
            );
        }
    }, [dataStore, deploymentOptions]);

    useEffect(() => {
        if (dataStore) {
            let cardData = [
                {
                    label: (
                        <div
                            className={
                                styles.DataStoreOverviewCloudInformationLabel
                            }
                        >
                            Provider
                        </div>
                    ),
                    testId: `${testId}CardProvider`,
                    content: (
                        <SmallContainer
                            topChildren={
                                <>
                                    {getCloudProviderLogo(
                                        dataStore.getCloudProvider()
                                    )}
                                    <div>
                                        {dataStore.getFullCloudProviderName()}
                                    </div>
                                </>
                            }
                        />
                    ),
                    span: 9,
                },
                {
                    label: (
                        <div
                            className={
                                styles.DataStoreOverviewCloudInformationLabel
                            }
                        >
                            Region
                        </div>
                    ),
                    testId: `${testId}CardRegion`,
                    content: (
                        <SmallContainer
                            topChildren={
                                <>
                                    {countryCode && (
                                        <AppFlagIcon code={countryCode} />
                                    )}
                                    <div>
                                        {dataStore?.isLintasarta()
                                            ? dataStore?.preferredRegion
                                            : dataStore?.cloudRegion}
                                    </div>
                                </>
                            }
                        />
                    ),
                    span: 8,
                },
                {
                    label: (
                        <div
                            className={
                                styles.DataStoreOverviewCloudInformationLabel
                            }
                        >
                            VPC
                        </div>
                    ),
                    testId: `${testId}CardVpc`,
                    content: (
                        <div>
                            {dataStore.vpc ? (
                                dataStore?.vpc?.getVpcName()
                            ) : (
                                <Tooltip
                                    placement="bottom"
                                    title="Only available on AWS private networks."
                                >
                                    Not available <CcxIconInfoCircleOutlined />
                                </Tooltip>
                            )}
                        </div>
                    ),
                    span: 7,
                },
            ];

            if (!dataStore.getCloudProvider().isAws()) {
                cardData.pop();
            }

            setCards(cardData);
        }
    }, [dataStore, countryCode]);

    return (
        <div
            className={styles.DataStoreOverviewCloudInformation}
            data-testid={testId}
        >
            <div className={styles.DataStoreOverviewCloudInformationContent}>
                {dataStore ? (
                    <>
                        <header>Cloud</header>
                        <main>
                            <Row
                                className={
                                    styles.DataStoreOverviewCloudInformationColumns
                                }
                            >
                                {cards.map((c: any, i: number) => {
                                    return (
                                        <Col span={c.span} key={`cards${i}`}>
                                            <SmallContainer
                                                testId={c.testId}
                                                topChildren={c.label}
                                                middleChildren={c.content}
                                            />
                                        </Col>
                                    );
                                })}
                            </Row>
                        </main>
                    </>
                ) : (
                    <LazyLoader
                        type="row"
                        testId={`${testId}Loader`}
                        rows={2}
                    />
                )}
            </div>
        </div>
    );
}

export default DataStoreOverviewCloudInformation;
